import { Box, Button, Container, Paper, Slider, Stack, ThemeProvider, Typography, useTheme } from '@mui/material';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { FlipCard } from '../../components/animation/FlipCard';
import { FullScreenMessage } from '../../components/animation/FullScreenMessage';
import { PhaseTransition } from '../../components/animation/PhaseTransition';
import { RoundTransition } from '../../components/animation/RoundTransition';
import { WelcomeSequence } from '../../components/animation/WelcomeSequence';
import { BackgroundDim } from '../../components/game/BackgroundDim';
import { FaceOffOverlay } from '../../components/game/FaceOffOverlay';
import { FaceOffPortalProvider } from '../../components/game/FaceOffPortal';
import { GameLogo } from '../../components/game/GameLogo';
import { SegmentScore } from '../../components/game/SegmentScore';
import { GameTheme } from '../../config/GameTheme';
import { mindColors } from '../../config/MindTheme';
import { AsyncAnimation, useAsyncAnimation } from '../../util/Animation';
import { muiStyleSheet } from '../../util/Styles';

export const AnimationScreen: FunctionComponent = () => {
	return <ThemeProvider theme={GameTheme}>
		<Container maxWidth="md" sx={styles.container}>
			<Typography variant="h2" gutterBottom>
				Animations
			</Typography>

			<Stack spacing={4}>
				<FlipCardDemo />
				<Transitions />
				<ScoreTest />
				<LogoDemo />
			</Stack>
		</Container>
	</ThemeProvider >;
};

const FlipCardDemo: FunctionComponent = () => {
	const [flipped, setFlipped] = useState(false);
	const theme = useTheme();

	return <Stack>
		<Typography variant="h4">
			FlipCard
		</Typography>
		<Typography variant="body2">
			Click the card to flip it.
		</Typography>
		<Box sx={{ marginTop: 2, maxWidth: 400 }}>
			<FlipCard
				flipped={flipped}
				onClick={() => setFlipped(!flipped)}
				style={{ height: '200px' }}

				front={<Paper elevation={3} sx={{
					...styles.flipCard,
					color: theme.palette.primary.contrastText,
					bgcolor: theme.palette.primary.main,
				}}
				>
					<Typography variant="h3">Front</Typography>
				</Paper>}

				back={<Paper elevation={3} sx={{
					...styles.flipCard,
					color: theme.palette.secondary.contrastText,
					bgcolor: theme.palette.secondary.main,
				}}>
					<Typography variant="h3">Back</Typography>
				</Paper>}
			/>
		</Box>
	</Stack>;
};

const Transitions: FunctionComponent = () => {
	const wipeAnim = useAsyncAnimation('Full Screen Wipe');
	const fadeAnim = useAsyncAnimation('Full Screen Fade');
	const dimAnim = useAsyncAnimation('Background Dim', { hideOnFinish: false });
	const welcomeAnim = useAsyncAnimation('Welcome Sequence');
	const roundAnim = useAsyncAnimation('New Round Transition');
	const phaseAnim = useAsyncAnimation('Phase Transition');
	const faceOffAnim = useAsyncAnimation('Face Off');

	return <FaceOffPortalProvider>
		<Stack spacing={1}>
			<Typography variant='h4'>Transitions</Typography>
			<AnimButton anim={wipeAnim} />
			<AnimButton anim={fadeAnim} />
			<ToggleAnimButton anim={dimAnim} />
			<AnimButton anim={welcomeAnim} />
			<AnimButton anim={roundAnim} />
			<AnimButton anim={phaseAnim} />
			<ToggleAnimButton anim={faceOffAnim} />

			<FullScreenMessage animationType='wipe' {...wipeAnim.props} sx={styles.wipe}>
				<Container>
					<Typography variant='h4'>Full Screen Wipe</Typography>
					<Typography>A full screen wipe transition</Typography>
				</Container>
			</FullScreenMessage>

			<FullScreenMessage animationType='fade' {...fadeAnim.props} sx={styles.wipe}>
				<Container>
					<Typography variant='h4'>Full Screen Fade</Typography>
					<Typography>A full screen fade transition</Typography>
				</Container>
			</FullScreenMessage>

			<WelcomeSequence {...welcomeAnim.props} />
			<BackgroundDim {...dimAnim.props} />
			<RoundTransition {...roundAnim.props} roundNumber={3} />

			<PhaseTransition phase='playOrPass' {...phaseAnim.props} />

			<FaceOffOverlay {...faceOffAnim.props} />
		</Stack>
	</FaceOffPortalProvider>;
};

const ScoreTest: FunctionComponent = () => {
	const [value, setValue] = useState(0);

	return <Stack spacing={1}>
		<Typography variant='h4'>Components</Typography>
		<SegmentScore value={value} digits={3} height='6em' />
		<Slider max={200} value={value} onChange={(_e, v) => setValue(v)} />
	</Stack>;
};

const LogoDemo: FunctionComponent = () => {
	const logoAnim = useAsyncAnimation('Game Logo', { hideOnFinish: false });

	return <Stack spacing={1}>
		<Typography variant='h4'>Logo</Typography>
		<ToggleAnimButton anim={logoAnim} />
		<Box sx={styles.logoContainer}>
			<GameLogo {...logoAnim.props} />
		</Box>
	</Stack>;
};

const AnimButton: FunctionComponent<PropsWithChildren<{ anim: AsyncAnimation; }>> = (props) =>
	<Button variant='outlined' onClick={() => props.anim.play()} sx={styles.button}>{props.anim.name}</Button>;

const ToggleAnimButton: FunctionComponent<PropsWithChildren<{ anim: AsyncAnimation; }>> = (props) => {
	const { anim } = props;
	const willReverse = anim.props.show && !anim.props.reverse;

	return <Button variant='outlined'
		onClick={() => anim.play({ direction: willReverse ? 'reverse' : 'forward' })}
		sx={styles.button}>
		{willReverse ? 'Hide' : 'Show'} {anim.name}
	</Button>;
};

const styles = muiStyleSheet({
	container: (theme) => ({
		marginTop: 4,
		paddingY: 2,
		borderRadius: 2,
		bgcolor: theme.palette.background.default,
		zIndex: 10,
	}),
	button: {
		width: 300,
	},
	flipCard: {
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	wipe: {
		color: mindColors.white,
		backgroundColor: mindColors.clay,
	},
	logoContainer: {
		width: 400,
		height: 400,
		bgcolor: 'black',
		borderRadius: 2,
	},
});